import apiService from "./api";
import i18n from "@/i18n";
import { ENTITY_MAP } from "@/config/entityMap";

const { t } = i18n.global;
const E = ENTITY_MAP;

/**
 * Interface para os itens de menu
 */
export interface MenuItem {
	id: string;
	title: string;
	path?: string;
	icon?: string;
	parentId?: string | null;
	order: number;
	adminOnly?: boolean;
	permissionId?: string | null;
	permission?: {
		id: string;
		name: string;
		description?: string;
	} | null;
	children?: MenuItem[];
}

/**
 * Serviço para gerenciamento de menus
 */
export default {
	/**
	 * Retorna menus estáticos para desenvolvimento/testes
	 */
	getDevMenus(): MenuItem[] {
		return [
			{
				id: "dashboard",
				title: t("navigation.dashboard"),
				path: "/dashboard",
				icon: E.dashboard.icon,
				order: 1,
			},
			{
				id: "people-management",
				title: t("menu.people.title"),
				icon: "mdi-account-group",
				order: 2,
				children: [
					{
						id: "consultants",
						title: t("navigation.consultants"),
						path: "/consultants",
						icon: E.consultant.icon,
						parentId: "people-management",
						order: 1,
					},
					{
						id: "mediums",
						title: t("navigation.mediums"),
						path: "/mediums",
						icon: E.medium.icon,
						parentId: "people-management",
						order: 2,
					},
					{
						id: "journeyStages",
						title: t("navigation.journeyStages"),
						path: "/journey-stages",
						icon: E.journeyStage.icon,
						parentId: "people-management",
						order: 3,
					},
					{
						id: "functions",
						title: t("navigation.functions"),
						path: "/functions",
						icon: E.function.icon,
						parentId: "people-management",
						order: 4,
					},
					{
						id: "member-groups",
						title: t("memberGroup.title"),
						path: "/member-groups",
						icon: E.memberGroup.icon,
						parentId: "people-management",
						order: 5,
					},
				],
			},
			{
				id: "spiritual-management",
				title: t("menu.spiritualManagement.title"),
				icon: "mdi-star",
				order: 3,
				children: [
					{
						id: "spiritual-lines",
						title: t("navigation.spiritualLines"),
						path: "/spiritual-lines",
						icon: E.spiritualLine.icon,
						parentId: "spiritual-management",
						order: 1,
					},
					{
						id: "guides",
						title: t("navigation.guides"),
						path: "/guides",
						icon: E.guide.icon,
						parentId: "spiritual-management",
						order: 2,
					},
					{
						id: "orixas",
						title: t("navigation.orixas"),
						path: "/orixas",
						icon: E.orixa.icon,
						parentId: "spiritual-management",
						order: 3,
					},
				],
			},
			{
				id: "ritual-management",
				title: t("menu.ritualManagement.title"),
				icon: "mdi-candle",
				order: 4,
				children: [
					{
						id: "attendance-types",
						title: t("navigation.attendanceTypes"),
						path: "/attendance-types",
						icon: E.attendanceType.icon,
						parentId: "ritual-management",
						order: 1,
					},
					{
						id: "ritual-types",
						title: t("navigation.ritualTypes"),
						path: "/ritual-types",
						icon: E.ritualType.icon,
						parentId: "ritual-management",
						order: 2,
					},
					{
						id: "rituals",
						title: t("navigation.rituals"),
						path: "/rituals",
						icon: E.ritual.icon,
						parentId: "ritual-management",
						order: 3,
					},
					{
						id: "rituals-report",
						title: t("reports.general.menuTitle"),
						path: "/rituals/report",
						icon: E.report?.icon || "mdi-file-chart",
						parentId: "ritual-management",
						order: 4,
						adminOnly: true,
					},
				],
			},
			{
				id: "financial-management",
				title: t("financial.title"),
				icon: E.financial?.icon || "mdi-cash-multiple",
				order: 5,
				adminOnly: true,
				children: [
					{
						id: "financial-dashboard",
						title: t("financial.menu.dashboard"),
						path: "/financial",
						icon: "mdi-view-dashboard",
						parentId: "financial-management",
						order: 1,
					},
					{
						id: "financial-funds",
						title: t("financial.menu.funds"),
						path: "/financial/funds",
						icon: E.fund?.icon || "mdi-cash-multiple",
						parentId: "financial-management",
						order: 2,
					},
					{
						id: "financial-movements",
						title: t("financial.menu.movements"),
						path: "/financial/movements",
						icon: E.finMovement?.icon || "mdi-cash-multiple",
						parentId: "financial-management",
						order: 3,
					},
					{
						id: "financial-reports",
						title: t("financial.menu.reports"),
						path: "/financial/reports",
						icon: E.report?.icon || "mdi-file-chart",
						parentId: "financial-management",
						order: 5,
					},
					],
			},
			{
				id: "temple-management",
				title: t("menu.templeManagement.title"),
				icon: E.temple.icon,
				order: 6,
				adminOnly: true,
				children: [
					{
						id: "temple-settings",
						title: t("navigation.templeSettings"),
						path: "/temple/settings",
						icon: E.templeSettings.icon,
						parentId: "temple-management",
						order: 1,
					},
					{
						id: "temple-users",
						title: t("navigation.users"),
						path: "/temple/users",
						icon: E.user.icon,
						parentId: "temple-management",
						order: 2,
					},
					{
						id: "temple-invites",
						title: t("navigation.templeInvites"),
						path: "/temple/invites",
						icon: E.userInvite.icon,
						parentId: "temple-management",
						order: 3,
					},
					{
						id: "billing",
						title: t("navigation.billing"),
						path: "/temple/plan",
						icon: E.plan.icon,
						parentId: "temple-management",
						order: 4,
					},
					{
						id: "financial-integrations",
						title: t("financial.menu.integrations"),
						path: "/financial/integrations",
						icon: "mdi-connection",
						parentId: "temple-management",
						order: 5,
					},
				],
			},
			{
				id: "tools",
				title: t("menu.tools.title"),
				icon: "mdi-toolbox",
				order: 7,
				adminOnly: true,
				children: [
					{
						id: "display-messages",
						title: t("navigation.displayMessages"),
						path: "/temple/display-messages",
						icon: E.displayMessage.icon,
						parentId: "tools",
						order: 1,
					},
					{
						id: "wisdom-quotes",
						title: t("navigation.wisdomQuotes"),
						path: "/temple/wisdom-quotes",
						icon: E.wisdomQuote.icon,
						parentId: "tools",
						order: 2,
					},
					{
						id: "temple-import",
						title: t("navigation.import"),
						path: "/temple/import",
						icon: E.import.icon,
						parentId: "tools",
						order: 3,
					},
				],
			},
			];
	},
	/**
	 * Obtém todos os itens de menu (estrutura de árvore)
	 */
	async getAllMenuItems(): Promise<MenuItem[]> {
		const response = await apiService.get("/menus");
		return response.menuItems;
	},

	/**
	 * Obtém todos os itens de menu (lista plana)
	 */
	async getAllMenuItemsFlat(): Promise<MenuItem[]> {
		const response = await apiService.get("/menus/flat");
		return response.menuItems;
	},

	/**
	 * Obtém um item de menu específico por ID
	 */
	async getMenuItemById(id: string): Promise<MenuItem> {
		const response = await apiService.get(`/menus/${id}`);
		return response.menuItem;
	},

	/**
	 * Cria um novo item de menu
	 */
	async createMenuItem(menuItem: Omit<MenuItem, "id">): Promise<MenuItem> {
		const response = await apiService.post("/menus", menuItem);
		return response.menuItem;
	},

	/**
	 * Atualiza um item de menu existente
	 */
	async updateMenuItem(
		id: string,
		menuItem: Partial<MenuItem>
	): Promise<MenuItem> {
		const response = await apiService.put(`/menus/${id}`, menuItem);
		return response.menuItem;
	},

	/**
	 * Exclui um item de menu
	 */
	async deleteMenuItem(id: string): Promise<void> {
		await apiService.delete(`/menus/${id}`);
	},

	/**
	 * Reordena itens de menu
	 */
	async reorderMenuItems(
		items: { id: string; order: number }[]
	): Promise<void> {
		await apiService.post("/menus/reorder", { items });
	},

	/**
	 * Obtém menus acessíveis ao usuário atual
	 */
	async getUserAccessibleMenus(): Promise<MenuItem[]> {
		// Retornar menus estáticos para desenvolvimento por enquanto
		return this.getDevMenus();

		try {
			// Tentar obter do backend
			const response = await apiService.get("/menus/user");
			return response.menuItems || [];
		} catch (error) {
			// Se falhar, retornar menus estáticos para desenvolvimento
			return this.getDevMenus();
		}
	},
};
